import { isBoolean, isNumber } from 'lodash'
import { baseApi } from './baseApi'

export const housesApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        /** 屋源詳細資料(house information)*/
        getHousesDetail: builder.query({
            query: (houseId) => ({
                url: `lessor/v1/houses/${houseId}`,
                method: 'GET',
            }),
            keepUnusedDataFor: 0,
            providesTags: ['HOUSES_DETAIL_POST', 'POST'],
        }),
        /** 更改合約備註 */
        updateHousesNote: builder.mutation({
            query: ({ houseId, note = '' }) => ({
                url: `lessor/v1/houses/${houseId}/note`,
                method: 'PUT',
                data: { note },
            }),
            invalidatesTags: ['HOUSES_DETAIL_POST'],
        }),
        // 獲取租金排程帳單資料
        getHousesScheduleRentBills: builder.query({
            query: ({ houseId, rentPayMethod }) => {
                return {
                    url: `lessor/v1/houses/schedule-rent-bills/${houseId}?rent_pay_method=${rentPayMethod}`,
                    method: 'GET',
                }
            },
            providesTags: ['POST'],
        }),
        // 更新租金排程帳單資料
        updateHousesScheduleRentBills: builder.mutation({
            query: ({ houseId, data }) => ({
                url: `lessor/v1/houses/schedule-rent-bills/${houseId}`,
                method: 'PUT',
                data,
            }),
            invalidatesTags: ['POST'],
        }),
        // 合約支援的繳費週期與繳費渠道
        getSupportPayMethodsAndChannels: builder.query({
            query: ({
                houseId,
                rent_start_at,
                rent_end_at,
                channel,
                method,
            }) => {
                const params = new URLSearchParams()

                if (rent_start_at) params.append('rent_start_at', rent_start_at)
                if (rent_end_at) params.append('rent_end_at', rent_end_at)
                if (isNumber(channel)) params.append('channel', channel)
                if (method) params.append('method', method)

                const queryString = params.toString()

                return {
                    url: `lessor/v1/houses/${houseId}/support-pay-methods-and-channels${queryString ? '?' + queryString : ''}`,
                    method: 'GET',
                }
            },
            providesTags: ['POST'],
        }),
        // 獲得合約電費度數的設定資訊
        getHouseSelectricBillConfigs: builder.query({
            query: ({ houseId }) => {
                return {
                    url: `lessor/v1/houses/${houseId}/electric-bill-configs`,
                    method: 'GET',
                }
            },
        }),
        updateHousePayChannel: builder.mutation({
            query: ({ houseId, payChannel, bankCode, bankAccount }) => {
                const data = { pay_channel: payChannel }
                if (payChannel === 9903) {
                    data.custom_atm_no = {
                        bank_code: bankCode,
                        bank_account: bankAccount,
                    }
                }
                return {
                    url: `lessor/v1/houses/${houseId}/change-pay-channel`,
                    method: 'POST',
                    data: data,
                }
            },
            invalidatesTags: ['POST'],
        }),
        // 獲得房屋繳費設定
        getHousePaymentConfig: builder.query({
            query: ({ houseId }) => ({
                url: `lessor/v1/houses/${houseId}/payment-config`,
                method: 'GET',
            }),
        }),
        // 從圖片獲取點交設備資訊
        getPlacesGetDevicesFromImage: builder.query({
            query: ({ houseId, image, use_text }) => {
                const formData = new FormData()

                image.forEach((file) => {
                    formData.append('image', file)
                })

                if (use_text) {
                    formData.append('use_text', use_text)
                }
                return {
                    url: `lessor/v1/houses/${houseId}/places/get-devices-from-image`,
                    method: 'POST',
                    data: formData,
                }
            },
        }),
        // 合約列表 - 出租已過期
        // params, take=10&skip=0&origin_house_id=-Hn_i-
        getHousesExpiredRentHouses: builder.query({
            query: (params) => {
                return {
                    url: `lessor/v1/houses/expired-rent-houses?${params}`,
                    method: 'GET',
                }
            },
            keepUnusedDataFor: 0,
        }),
        /* 創建屋源(create house) */
        createHouse: builder.mutation({
            query: (data) => {
                const formData = new FormData()
                const { syncValues, ...restData } = data
                Object.keys(restData).forEach((key) => {
                    if (isBoolean(restData[key]) || restData[key]) {
                        formData.append(key, restData[key])
                    }
                })
                const syncValuesString = JSON.stringify(syncValues).slice(1, -1)

                formData.append('syncValues', syncValuesString)

                return {
                    url: 'lessor/v1/houses',
                    method: 'POST',
                    data: formData,
                }
            },
        }),
        /* 更新屋源詳細資料(update house information) */
        updateHouseDetail: builder.mutation({
            query: (data) => {
                const formData = new FormData()
                const { syncValues, id, ...restData } = data
                Object.keys(restData).forEach((key) => {
                    if (isBoolean(restData[key]) || restData[key]) {
                        formData.append(key, restData[key])
                    }
                })
                const syncValuesString = JSON.stringify(syncValues).slice(1, -1)

                formData.append('syncValues', syncValuesString)

                return {
                    url: `lessor/v1/houses/${id}`,
                    method: 'PATCH',
                    data: formData,
                }
            },
        }),
        // DELETE /lessor/v1/houses/{house_id} 刪除屋源(delete house)
        deleteHouse: builder.mutation({
            query: (houseId) => ({
                url: `lessor/v1/houses/${houseId}`,
                method: 'DELETE',
            }),
        }),
        // /lessor/v1/houses/{house_id}/archive 封存合約
        archiveHouse: builder.mutation({
            query: (houseId) => ({
                url: `lessor/v1/houses/${houseId}/archive`,
                method: 'POST',
            }),
        }),
        /**
         * /lessor/v1/houses/{house_id}/validate-can-sign-contract
         * 簽約流程 - 驗證是否能夠簽約
         */
        validateCanSignContract: builder.query({
            query: (houseId) => ({
                url: `lessor/v1/houses/${houseId}/validate-can-sign-contract`,
                method: 'GET',
            }),
        }),
        // 簽約流程 - 房東個人簽約(只限傳統紙本合約類型)
        signTraditionalPaperContract: builder.mutation({
            query: ({ houseId, ...data }) => ({
                url: `lessor/v1/houses/${houseId}/sign-traditional-paper-contract`,
                method: 'POST',
                data,
            }),
        }),
        // 更新合約金流設定檔 - 自訂轉帳銀行帳號
        updateHousePaymentConfigCustomAtmNo: builder.mutation({
            query: ({ houseId, ...data }) => {
                return {
                    url: `lessor/v1/houses/${houseId}/payment-config/custom-atm-no/sync`,
                    method: 'POST',
                    data,
                }
            },
        }),
        // 合約歷程紀錄
        getHousesHistories: builder.query({
            keepUnusedDataFor: 0,
            query: (houseId) => ({
                url: `lessor/v1/houses/${houseId}/histories`,
                method: 'GET',
            }),
        }),
        // GET /lessor/v1/houses/{house_id}/places 獲取合約場景與設備群 - 適用點交
        getHousesPlacesByHouseId: builder.query({
            query: (houseId) => ({
                url: `lessor/v1/houses/${houseId}/places`,
                method: 'GET',
            }),
        }),
    }),
})
export const {
    useGetHousesDetailQuery,
    useLazyGetHousesDetailQuery,
    useUpdateHousesNoteMutation,
    useGetHousesScheduleRentBillsQuery,
    useUpdateHousesScheduleRentBillsMutation,
    useGetSupportPayMethodsAndChannelsQuery,
    useLazyGetSupportPayMethodsAndChannelsQuery,
    useGetHouseSelectricBillConfigsQuery,
    useUpdateHousePayChannelMutation,
    useGetHousePaymentConfigQuery,
    useLazyGetPlacesGetDevicesFromImageQuery,
    useLazyGetHousesExpiredRentHousesQuery,
    useCreateHouseMutation,
    useUpdateHouseDetailMutation,
    useDeleteHouseMutation,
    useArchiveHouseMutation,
    useLazyValidateCanSignContractQuery,
    useSignTraditionalPaperContractMutation,
    useUpdateHousePaymentConfigCustomAtmNoMutation,
    useGetHousesHistoriesQuery,
    useLazyGetHousesPlacesByHouseIdQuery,
} = housesApi
